import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_PREFIX = "scrollpos:";

// All of this app's layouts (Admin/Customer/Rider) scroll an inner
// `.app-scroll-container` Box, not the window itself — the shell is a fixed
// `height: 100vh; overflow: hidden` frame around the sidebar/header, so
// `window` never scrolls and `window.scrollY` is always 0. Falls back to
// `window` for any page rendered outside those layouts.
const getScrollContainer = () => document.querySelector(".app-scroll-container") || window;
const getScrollTop = (el) => (el === window ? window.scrollY : el.scrollTop);
const scrollTo = (el, top) => (el === window ? window.scrollTo(0, top) : (el.scrollTop = top));

// Restores scroll position when navigating BACK to a page React Router
// already unmounted and is now remounting fresh — BrowserRouter doesn't do
// this itself (unlike a full browser reload, which the OS/browser handles
// natively), so a long list scrolled halfway down snaps back to the top
// every time an admin returns to it via Back. Keyed by `location.key`
// (React Router's own unique id for THIS history entry) rather than the
// pathname, so two different visits to the same route (different scroll
// depths, different times) never collide or restore a stale value onto the
// wrong visit. sessionStorage, not component state, since this has to
// survive the full unmount the page just went through.
export default function useScrollRestoration() {
  const location = useLocation();
  const storageKey = `${STORAGE_PREFIX}${location.key}`;

  useEffect(() => {
    const container = getScrollContainer();
    const saved = sessionStorage.getItem(storageKey);
    if (saved != null) {
      // Table rows may not have laid out yet on the very first paint —
      // one rAF is enough to wait for that without a visible delay/jump.
      requestAnimationFrame(() => scrollTo(container, parseInt(saved, 10)));
    }

    const handleScroll = () => {
      sessionStorage.setItem(storageKey, String(getScrollTop(container)));
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [storageKey]);
}
